'use client';

import {
  RefObject,
  SVGProps,
  createContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/cn';
import { motion, MotionConfig, useReducedMotion } from 'motion/react';

import { Container } from '@/components/Container';
import { SaplingLogo } from '@/components/Logo';
import { Addresses } from '@/components/Addresses';
import { SocialMedia } from '@/components/SocialMedia';
import { MainNav } from './MainNav';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useCart } from '@/components/ecommerce/cart-provider';

const RootLayoutContext = createContext({});

type RootLayoutProps = {
  children: React.ReactNode;
  transparent?: boolean;
};

export function RootLayout({ children, transparent = false }: RootLayoutProps) {
  const pathname = usePathname();
  const [logoHovered, setLogoHovered] = useState(false);

  return (
    <RootLayoutContext.Provider value={{ logoHovered, setLogoHovered }}>
      <RootLayoutInner invert={transparent} key={pathname}>
        {children}
      </RootLayoutInner>
    </RootLayoutContext.Provider>
  );
}

type RootLayoutInnerProps = {
  children: React.ReactNode;
  invert?: boolean;
};

function RootLayoutInner({ children, invert }: RootLayoutInnerProps) {
  const panelId = useId();
  const [expanded, setExpanded] = useState(false);
  const openRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (
        (event.target as HTMLElement | null)?.closest('a')?.href ===
        window.location.href
      ) {
        setExpanded(false);
      }
    }

    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('click', onClick);
    };
  }, []);

  return (
    <MotionConfig transition={shouldReduceMotion ? { duration: 0 } : undefined}>
      <header>
        <div
          className="absolute top-2 right-0 left-0 z-40"
          aria-hidden={expanded ? 'true' : undefined}
          inert={expanded ? true : undefined}
        >
          <Header
            panelId={panelId}
            icon={MenuIcon}
            toggleRef={openRef}
            expanded={expanded}
            invert={invert}
            onToggle={() => {
              setExpanded((expanded) => !expanded);
              window.setTimeout(() =>
                closeRef.current?.focus({ preventScroll: true }),
              );
            }}
          />
        </div>

        <motion.div
          layout
          id={panelId}
          style={{ height: expanded ? 'auto' : '0.5rem' }}
          className="relative z-50 overflow-hidden bg-neutral-200 pt-2"
          aria-hidden={expanded ? undefined : 'true'}
          inert={expanded ? undefined : true}
        >
          <motion.div layout className="bg-neutral-200">
            <div ref={navRef} className="bg-neutral-200 pb-16">
              <Header
                invert
                panelId={panelId}
                icon={XIcon}
                toggleRef={closeRef}
                expanded={expanded}
                onToggle={() => {
                  setExpanded((expanded) => !expanded);
                  window.setTimeout(() =>
                    openRef.current?.focus({ preventScroll: true }),
                  );
                }}
              />
            </div>
            <Navigation />
            <div className="relative bg-neutral-200 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-neutral-300">
              <Container>
                <div className="grid grid-cols-1 gap-y-10 pt-10 pb-16 sm:grid-cols-2 sm:pt-16">
                  <div>
                    {/* <h2 className="font-display text-base font-semibold text-neutral-700">
                      Kontaktuppgifter
                    </h2> */}
                    <Addresses
                      invert={false}
                      className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2"
                    />
                  </div>
                  <div className="sm:border-l sm:border-neutral-300 sm:pl-16">
                    <h2 className="font-display text-base font-semibold text-neutral-700">
                      Följ oss
                    </h2>
                    <SocialMedia className="mt-6" invert={false} />
                  </div>
                </div>
              </Container>
            </div>
          </motion.div>
        </motion.div>
      </header>

      <motion.div
        layout
        style={{ borderTopLeftRadius: 40, borderTopRightRadius: 40 }}
        className="relative flex flex-auto overflow-hidden bg-neutral-200"
      >
        <motion.div
          layout
          className="relative isolate flex w-full flex-col bg-transparent"
        >
          {/* <GridPattern
            className="absolute inset-x-0 -top-14 -z-10 h-[1000px] w-full fill-neutral-50 stroke-neutral-950/5 [mask-image:linear-gradient(to_bottom_left,white_40%,transparent_50%)]"
            yOffset={-96}
            interactive
          /> */}

          <div className="w-full flex-auto">{children}</div>
        </motion.div>
      </motion.div>
    </MotionConfig>
  );
}

function XIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="m5.636 4.223 14.142 14.142-1.414 1.414L4.222 5.637z" />
      <path d="M4.222 18.363 18.364 4.22l1.414 1.414L5.636 19.777z" />
    </svg>
  );
}

function MenuIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M2 6h20v2H2zM2 16h20v2H2z" />
    </svg>
  );
}

type HeaderProps = {
  panelId: string;
  invert?: boolean;
  icon: React.ComponentType<SVGProps<SVGSVGElement>>;
  expanded: boolean;
  onToggle: () => void;
  toggleRef: RefObject<HTMLButtonElement | null>;
};

function Header({
  panelId,
  invert = false,
  icon: Icon,
  expanded,
  onToggle,
  toggleRef,
}: HeaderProps) {
  const isCloseButton = Icon === XIcon;
  const showGreenLogo = isCloseButton;
  const { totalItems, openCart } = useCart();

  return (
    <Container>
      <div className="flex items-center justify-between pt-14">
        <Link href="/" aria-label="Home">
          <SaplingLogo
            className="h-14 sm:h-20"
            variant={invert && !showGreenLogo ? 'white' : 'gold'}
          />
        </Link>
        <div className="flex items-center gap-x-6">
          {!expanded ? <MainNav transparent={invert} /> : null}
          {!expanded && (
            <button
              type="button"
              onClick={openCart}
              className={cn(
                'relative -m-2.5 rounded-full p-2.5 transition lg:hidden',
                invert ? 'hover:bg-white/10' : 'hover:bg-neutral-300',
              )}
              aria-label="Kundvagn"
            >
              <ShoppingBagIcon
                className={cn(
                  'h-6 w-6',
                  invert ? 'text-white' : 'text-neutral-700',
                )}
              />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-600 px-1 text-xs font-bold text-white">
                  {totalItems}
                </span>
              )}
            </button>
          )}
          <button
            ref={toggleRef}
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={`${panelId}-button`}
            className={cn(
              'group -m-2.5 rounded-full p-2.5 transition',
              isCloseButton
                ? 'bg-orange-600 hover:bg-orange-700'
                : invert
                  ? 'hover:bg-white/10'
                  : 'hover:bg-neutral-300',
            )}
            aria-label="Toggle navigation"
          >
            <Icon
              className={cn(
                'h-6 w-6',
                isCloseButton
                  ? 'fill-white'
                  : invert
                    ? 'fill-white group-hover:fill-neutral-200'
                    : 'fill-neutral-700 group-hover:fill-neutral-500',
              )}
            />
          </button>
        </div>
      </div>
    </Container>
  );
}

function NavigationRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="even:mt-px sm:bg-neutral-200">
      <Container className={''}>
        <div className="grid grid-cols-1 sm:grid-cols-2">{children}</div>
      </Container>
    </div>
  );
}

function NavigationItem({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="group relative isolate -mx-6 bg-neutral-200 px-6 py-10 even:mt-px sm:mx-0 sm:px-0 sm:py-16 sm:odd:pr-16 sm:even:mt-0 sm:even:border-l sm:even:border-neutral-300 sm:even:pl-16"
    >
      {children}
      <span className="absolute inset-y-0 -z-10 w-screen bg-orange-50 opacity-0 transition group-odd:right-0 group-even:left-0 group-hover:opacity-100" />
    </Link>
  );
}

// Needs to be in groups of two items per row
function Navigation() {
  return (
    <nav className="font-display mt-px text-lg font-medium tracking-tight text-neutral-700">
      <NavigationRow>
        <NavigationItem href="/om-stiftelsen">Om stiftelsen</NavigationItem>
        <NavigationItem href="/fond">Fond</NavigationItem>
      </NavigationRow>
      <NavigationRow>
        <NavigationItem href="/forlag">Förlag</NavigationItem>
        <NavigationItem href="/forandringsarbete">
          Förändringsarbete
        </NavigationItem>
      </NavigationRow>
      <NavigationRow>
        <NavigationItem href="/fond#stod-oss">Stöd oss</NavigationItem>
        <NavigationItem href="/fond#ansok-om-stod">
          Ansök om stöd
        </NavigationItem>
      </NavigationRow>
      <NavigationRow>
        <NavigationItem href="/nyheter-och-uppdateringar">
          Nyheter och uppdateringar
        </NavigationItem>
        <NavigationItem href="/kontakta-oss">Kontakta oss</NavigationItem>
      </NavigationRow>
    </nav>
  );
}
